import { getLessons } from '@/app/(app)/learn/learn-data'
import { COMMUNITY_PAGE_COPY, LEARN_INDEX_COPY } from '@/app/(app)/learn/learn-copy'
import { AVOIDED_GLOSSARY_KEYS, GLOSSARY, getLocalizedGlossaryEntry, type GlossaryKey } from '@/lib/features/glossary'
import {
  PUBLIC_SHORTCUTS,
  getLocalizedPublicShortcutText,
  type PublicShortcutEntry,
  type PublicShortcutLocale,
} from '@/lib/features/public-shortcuts'
import { getDocsPages, getDocsSections, docPath, type DocsLocale } from './index'

export interface LearnSearchItem {
  id: string
  category: 'video' | 'doc' | 'shortcut'
  title: string
  description: string
  href: string
  meta?: string
  section?: string
  searchText?: string
}

export function buildLearnSearchIndex(locale: DocsLocale): LearnSearchItem[] {
  const items: LearnSearchItem[] = []
  const sectionTitleMap = new Map(getDocsSections(locale).map(section => [section.id, section.title]))

  for (const lesson of getLessons(locale)) {
    items.push({
      id: `video-${lesson.slug}`,
      category: 'video',
      title: lesson.title,
      description: lesson.description,
      href: `/learn/tutorials/${lesson.slug}`,
      meta: lesson.duration,
    })
  }

  for (const page of getDocsPages(locale)) {
    items.push({
      id: `doc-${page.slug}`,
      category: 'doc',
      title: page.title,
      description: page.description,
      href: docPath(page.slug),
      section: page.overview ?? undefined,
      meta: sectionTitleMap.get(page.section) ?? page.section,
      searchText: page.searchText,
    })
  }

  for (const key of Object.keys(GLOSSARY) as GlossaryKey[]) {
    if (AVOIDED_GLOSSARY_KEYS.includes(key)) continue
    const entry = getLocalizedGlossaryEntry(key, locale)
    items.push({
      id: `glossary-${key}`,
      category: 'doc',
      title: entry.term,
      description: entry.definition,
      href: `/learn/glossary#${key}`,
      meta: LEARN_INDEX_COPY[locale].glossary,
      searchText: entry.usageRules,
    })
  }

  items.push(
    {
      id: 'skill-blender',
      category: 'doc',
      title: 'Blender → Martini',
      description: LEARN_INDEX_COPY[locale].skillsDescription,
      href: '/skills/blender',
      meta: LEARN_INDEX_COPY[locale].skills,
    },
    {
      id: 'community',
      category: 'video',
      title: COMMUNITY_PAGE_COPY[locale].title,
      description: COMMUNITY_PAGE_COPY[locale].intro,
      href: '/learn/community',
      meta: LEARN_INDEX_COPY[locale].community,
    },
  )

  for (const shortcut of PUBLIC_SHORTCUTS as readonly PublicShortcutEntry[]) {
    const shortcutText = getLocalizedPublicShortcutText(shortcut, locale as PublicShortcutLocale)
    items.push({
      id: `shortcut-${shortcut.id}`,
      category: 'shortcut',
      title: shortcut.shortcut,
      description: shortcutText.description ?? shortcutText.label,
      href: '/learn/shortcuts',
    })
  }

  return items
}
